/**
 * §18.7 — the TWC quarterly worksheet: the figures for the Texas Workforce
 * Commission's Employer's Quarterly Report and its wage list (payroll stage 4).
 *
 * A WORKSHEET, AND IT SAYS SO FIRST, as the Form 941 worksheet does. The
 * figures are GET /payroll/forms/twc's (apps/api/src/payroll/twc-quarterly.ts,
 * from TWC's own definitions); TWC takes the report through its own portal,
 * and nothing here files it, pays it or marks it filed.
 *
 * TWC'S TAX DUE, AND WHAT THE RUNS BOOKED. TWC multiplies the quarter's
 * taxable wages by the rate once; the runs rounded pay by pay. When the two
 * differ the screen says both, since the payment TWC asks for is its own.
 *
 * NO SOCIAL SECURITY NUMBERS. The wage list names each employee and leads to
 * their page, where the number is.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { InfoNote } from '../components/InfoNote.js';
import { ApiError, api, type TwcReport, type WorksheetQuarter } from '../api.js';
import { formatCents } from '../lib/money.js';
import { formatRateMillionths } from '../lib/rates.js';
import { settingsTab } from '../lib/settings-tabs.js';

const MONTHS = [
  ['January', 'February', 'March'],
  ['April', 'May', 'June'],
  ['July', 'August', 'September'],
  ['October', 'November', 'December'],
] as const;

export function TwcPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const yearParam = searchParams.get('year');
  const quarterParam = searchParams.get('quarter');
  const year = yearParam === null ? undefined : Number(yearParam);
  const quarter = quarterParam === null ? undefined : Number(quarterParam);
  const [report, setReport] = useState<TwcReport | null>(null);
  const [quarters, setQuarters] = useState<WorksheetQuarter[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .getTwcWorksheet(year, quarter)
      .then((result) => {
        if (!live) return;
        setReport(result.report);
        setQuarters(result.quarters);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (live) setError(caught instanceof ApiError ? caught.message : 'Could not build the worksheet.');
      });
    return () => {
      live = false;
    };
  }, [year, quarter]);

  function choose(value: string) {
    const [nextYear, nextQuarter] = value.split('-');
    const params = new URLSearchParams(searchParams);
    params.set('year', nextYear!);
    params.set('quarter', nextQuarter!);
    setSearchParams(params, { replace: true });
  }

  /*
   * The server's list, anchored on today and newest first. Built here from the
   * year on show it offered that year and the one before, so a quarter of last
   * year, once chosen, took this year off the list (reported 2026-09-23).
   */
  const choices = quarters.map((each) => ({ value: `${each.year}-${each.quarter}`, label: each.label }));

  return (
    <section className="twc">
      <p>
        <Link to="/payroll">Back to Payroll</Link>
      </p>
      <h1>TWC Quarterly Worksheet</h1>

      {choices.length > 0 && report !== null && (
        <div className="row report-period">
          <label>
            Quarter{' '}
            <select value={`${report.quarter.year}-${report.quarter.quarter}`} onChange={(event) => choose(event.target.value)}>
              {choices.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {report === null && error === null && <p className="muted">Loading…</p>}

      {report !== null && error === null && <Worksheet report={report} />}
    </section>
  );
}

function Worksheet({ report }: { report: TwcReport }) {
  const { worksheet: w, quarter } = report;
  const months = MONTHS[quarter.quarter - 1];
  const difference = report.books.ledgerCents - report.books.runsCents;

  return (
    <>
      <p className="notice">
        A worksheet: the figures for TWC's Employer's Quarterly Report and wage list for {quarter.label}, drawn from the
        quarter's posted pay runs. Nothing here files the report or pays anything; TWC takes it through its own portal.
      </p>

      {report.draftRuns.length > 0 && (
        <div className="notice notice-warn">
          {report.draftRuns.length === 1 ? 'One run in this quarter is' : `${report.draftRuns.length} runs in this quarter are`}{' '}
          still a draft and not counted:
          <ul className="missed-list">
            {report.draftRuns.map((run) => (
              <li key={run.runId}>
                <Link to={`/payroll/runs/${run.runId}`}>
                  {run.employeeName}, paid {run.payDate}
                </Link>
              </li>
            ))}
          </ul>
          Post {report.draftRuns.length === 1 ? 'it' : 'them'}, then read this worksheet again.
        </div>
      )}

      {difference !== 0 && (
        <p className="notice notice-warn">
          The books hold {formatCents(report.books.ledgerCents)} of Texas unemployment tax for {quarter.label}, and the
          quarter's runs {formatCents(report.books.runsCents)}.{' '}
          {difference > 0
            ? 'Tax accrued outside a run is not on this worksheet.'
            : "A run's entry no longer books everything the run calculated."}{' '}
          The worksheet is the runs'; decide which is right before filing.
        </p>
      )}

      <section className="settings-section" aria-labelledby="twc-report">
        <h2 id="twc-report">
          Employer's Quarterly Report{' '}
          <InfoNote label="How Employment Is Counted">
            Each month counts the employees who worked during, or were paid for, the pay period including its 12th. The
            pay period is taken from each run's frequency: a quarterly run's is its calendar quarter, a monthly run's its
            calendar month, and a weekly or biweekly run's the seven or fourteen days ending on its pay date. Check the
            counts if a pay period ends on another day.
          </InfoNote>
        </h2>
        <div className="table-scroll">
          <table className="lines" aria-label="Employer's Quarterly Report">
            <tbody>
              <tr>
                <th scope="row">Total wages paid</th>
                <td className="num">{formatCents(w.totalWagesCents)}</td>
              </tr>
              <tr>
                <th scope="row">Taxable wages paid</th>
                <td className="num">{formatCents(w.taxableWagesCents)}</td>
              </tr>
              <tr>
                <th scope="row">Tax rate</th>
                <td className="num">{w.rateMillionths === null ? 'Not recorded' : formatRateMillionths(w.rateMillionths)}</td>
              </tr>
              <tr>
                <th scope="row">Taxes due</th>
                <td className="num">{w.taxDueCents === null ? 'Needs the rate' : formatCents(w.taxDueCents)}</td>
              </tr>
              {months.map((month, index) => (
                <tr key={month}>
                  <th scope="row">Employees, {month} 12</th>
                  <td className="num">{w.employment[index]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {w.rateMillionths === null && w.taxableWagesCents > 0 && (
          <p className="notice notice-warn">
            No Texas unemployment rate is recorded for {quarter.year}. <Link to={settingsTab('payroll')}>Record It in Settings</Link>
          </p>
        )}
        {w.taxDueCents !== null && w.taxDueCents !== w.taxBookedCents && (
          <p className="muted">
            TWC figures the tax on the quarter's taxable wages at once, {formatCents(w.taxDueCents)}; the runs booked{' '}
            {formatCents(w.taxBookedCents)}, rounding pay by pay. TWC asks for its own figure.
          </p>
        )}
        {w.taxableWagesCents === 0 && w.totalWagesCents > 0 && (
          <p className="muted">
            Nothing is taxable: each employee's first $9,000 for {quarter.year} was paid before this quarter. The report
            is still filed.
          </p>
        )}
      </section>

      <section className="settings-section" aria-labelledby="twc-wages">
        <h2 id="twc-wages">Wage List</h2>
        {w.employees.length === 0 ? (
          <p className="muted">No posted pay runs in {quarter.label}. The report is still filed, with no wages.</p>
        ) : (
          <>
            <p className="muted">Each employee's Social Security number is on their own page.</p>
            <div className="table-scroll">
              <table className="lines" aria-label="Wage List">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Employed In</th>
                    <th className="num">Gross Wages</th>
                    <th className="num">Taxable Wages</th>
                  </tr>
                </thead>
                <tbody>
                  {w.employees.map((employee) => (
                    <tr key={employee.employeeId}>
                      <td>
                        <Link to={`/payroll/employees/${employee.employeeId}`}>{employee.employeeName}</Link>
                      </td>
                      <td>{months.filter((_, index) => employee.months[index]).join(', ') || 'No month'}</td>
                      <td className="num">{formatCents(employee.grossWagesCents)}</td>
                      <td className="num">{formatCents(employee.taxableWagesCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <section className="settings-section" aria-labelledby="twc-paid">
        <h2 id="twc-paid">Paid to TWC</h2>
        {report.obligation === null ? (
          <p className="muted">The books hold no Texas unemployment tax for {quarter.label}.</p>
        ) : (
          <>
            <p>
              {formatCents(report.obligation.owedCents)} owed
              {report.obligation.dueDate !== null && (
                <>
                  , due <span className="date-cell">{report.obligation.dueDate}</span>
                </>
              )}
              ; {formatCents(report.obligation.paidCents)} paid; {formatCents(report.obligation.outstandingCents)} outstanding.
            </p>
            {report.obligation.payments.length > 0 && (
              <ul className="history-list" aria-label="Payments to TWC">
                {report.obligation.payments.map((payment) => (
                  <li key={payment.headerId}>
                    <Link to={`/journals/${payment.headerId}`}>
                      {payment.counterparty ?? 'A payment'} on <span className="date-cell">{payment.paidOn}</span>
                    </Link>{' '}
                    · {formatCents(payment.cents)}
                    {!payment.posted && ' (a draft)'}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>
    </>
  );
}
